import { CheckCircle2, Loader2, MailX, TriangleAlert } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { hostFollowApi, HostFollowApiError, type UnfollowContext } from '../services/hostFollowApi';

type Phase = 'loading' | 'invalid' | 'expired' | 'confirm' | 'already' | 'done' | 'allStopped';

/**
 * Token-authenticated unfollow page, reached from the footer link of a host
 * update email. No login: the signed token in the URL is the authorization.
 * The link lands on the API first, which redirects here with the token intact.
 */
export default function UnfollowHost() {
  const { t } = useTranslation('hostFollow');
  const [searchParams] = useSearchParams();
  // Read once. The token is scrubbed from the address bar below, so reading it
  // from the live query string on a later render would come back empty.
  const [token] = useState(() => searchParams.get('token') || '');

  const [phase, setPhase] = useState<Phase>('loading');
  const [context, setContext] = useState<UnfollowContext | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Take the token out of the address bar as soon as it is captured. It stays
  // valid for months, so leaving it in the URL leaves it in browser history and
  // in the referrer of anything the page links out to.
  useEffect(() => {
    if (!token) return;
    window.history.replaceState(window.history.state, '', window.location.pathname);
  }, [token]);

  useEffect(() => {
    if (!token) {
      setPhase('invalid');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await hostFollowApi.getUnfollowContext(token);
        if (cancelled) return;
        setContext(data);
        setPhase(data.following ? 'confirm' : 'already');
      } catch (err) {
        if (cancelled) return;
        // An expired link is a different story from a broken one: the user did
        // nothing wrong and just needs to sign in.
        const expired = err instanceof HostFollowApiError && err.code === 'EXPIRED_TOKEN';
        setPhase(expired ? 'expired' : 'invalid');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const run = useCallback(
    async (action: () => Promise<void>, nextPhase: Phase) => {
      setBusy(true);
      setError('');
      try {
        await action();
        setPhase(nextPhase);
      } catch (err) {
        if (err instanceof HostFollowApiError && err.code === 'EXPIRED_TOKEN') {
          setPhase('expired');
          return;
        }
        setError(t('unfollowPage.error'));
      } finally {
        setBusy(false);
      }
    },
    [t]
  );

  const host = context?.hostDisplayName ?? '';

  const shell = (icon: React.ReactNode, title: string, body: string, actions?: React.ReactNode) => (
    <div className="min-h-screen flex items-center justify-center bg-warm-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-xl shadow-brand p-8 text-center space-y-4">
          <div className="flex justify-center">{icon}</div>
          <h1 className="text-h3 font-bold text-charcoal">{title}</h1>
          <p className="text-body text-charcoal/70">{body}</p>
          {error && <p className="text-body text-red-600">{error}</p>}
          {actions && <div className="pt-2 space-y-3">{actions}</div>}
        </div>
      </div>
    </div>
  );

  const primaryButton = (label: string, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="w-full inline-flex items-center justify-center rounded-lg bg-primary px-6 py-3 font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-60"
    >
      {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
      {label}
    </button>
  );

  const secondaryLink = (to: string, label: string) => (
    <Link
      to={to}
      className="block font-semibold text-primary transition-colors hover:text-primary/80"
    >
      {label}
    </Link>
  );

  /**
   * The wider opt-out, offered alongside the per-host one. It has to be
   * reachable here rather than behind a login: stopping every promotional email
   * from a sender is expected to take nothing more than visiting a web page.
   *
   * Hidden once it has already been exercised, and labelled with everything it
   * covers so nobody stops the newsletter by accident while dropping one host.
   */
  const stopAllOption = () => {
    if (context?.optedOutOfAll) return null;
    return (
      <div className="pt-4 text-left">
        <button
          type="button"
          onClick={() => run(() => hostFollowApi.optOutOfAllByToken(token), 'allStopped')}
          disabled={busy}
          className="w-full rounded-lg border border-charcoal/20 px-6 py-3 font-semibold text-charcoal transition-colors hover:bg-charcoal/5 disabled:opacity-60"
        >
          {t('unfollowPage.stopAllCta')}
        </button>
        <p className="pt-2 text-small text-charcoal/60">{t('unfollowPage.stopAllHint')}</p>
      </div>
    );
  };

  if (phase === 'loading') {
    return shell(
      <Loader2 className="h-10 w-10 animate-spin text-primary" />,
      t('unfollowPage.loading'),
      ''
    );
  }

  if (phase === 'invalid') {
    return shell(
      <TriangleAlert className="h-10 w-10 text-amber-500" />,
      t('unfollowPage.invalidTitle'),
      t('unfollowPage.invalidBody'),
      secondaryLink('/dashboard', t('unfollowPage.dashboardCta'))
    );
  }

  if (phase === 'expired') {
    return shell(
      <TriangleAlert className="h-10 w-10 text-amber-500" />,
      t('unfollowPage.expiredTitle'),
      t('unfollowPage.expiredBody'),
      secondaryLink('/dashboard', t('unfollowPage.dashboardCta'))
    );
  }

  if (phase === 'confirm') {
    return shell(
      <MailX className="h-10 w-10 text-primary" />,
      t('unfollowPage.confirmTitle', { host }),
      t('unfollowPage.confirmBody', { host }),
      <>
        {primaryButton(t('unfollowPage.confirmCta'), () =>
          run(() => hostFollowApi.unfollowByToken(token), 'done')
        )}
        {stopAllOption()}
        {secondaryLink('/activities', t('unfollowPage.browseCta'))}
      </>
    );
  }

  if (phase === 'already') {
    return shell(
      <CheckCircle2 className="h-10 w-10 text-primary" />,
      t('unfollowPage.alreadyTitle', { host }),
      t('unfollowPage.alreadyBody'),
      <>
        {stopAllOption()}
        {secondaryLink('/activities', t('unfollowPage.browseCta'))}
      </>
    );
  }

  if (phase === 'allStopped') {
    return shell(
      <CheckCircle2 className="h-10 w-10 text-primary" />,
      t('unfollowPage.stopAllDoneTitle'),
      t('unfollowPage.stopAllDoneBody'),
      secondaryLink('/activities', t('unfollowPage.browseCta'))
    );
  }

  // phase === 'done'
  return shell(
    <CheckCircle2 className="h-10 w-10 text-primary" />,
    t('unfollowPage.doneTitle', { host }),
    // Says where to refollow: the token is opt-out only, so undo would need
    // a login anyway.
    t('unfollowPage.doneBody'),
    <>
      {/* Still offered: dropping one host may not have been the whole ask. */}
      {stopAllOption()}
      {secondaryLink('/dashboard', t('unfollowPage.dashboardCta'))}
    </>
  );
}
